import { Dialog, Transition } from "@headlessui/react";
import React, { Fragment, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import CloseIcon from "../../assets/icons/close-icon";
import AddButton, { DraftButton } from "../buttons/button";
import { invoiceDiscountUpdateAction } from "../../redux/actions/invoices-action";
import successToast from "../helper-components/success-toast";
import errorToast from "../helper-components/error-toast";

// Changes only the invoice discount of a finished bill — as a % of the item
// total or a ₹ amount. Opened from the bill list and the view-bill footer for
// users with the billing "Edit Discount" permission (the backend checks it too).
const EditInvoiceDiscountModal = ({
  isOpen,
  sotId,
  billNumber = "",
  initialIsPercentage = false,
  initialValue = "",
  onClose = () => {},
  onSaved = () => {},
}) => {
  const dispatch = useDispatch();
  const [isPercentage, setIsPercentage] = useState(initialIsPercentage);
  const [value, setValue] = useState(initialValue);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsPercentage(initialIsPercentage);
      setValue(initialValue ?? "");
    }
  }, [isOpen, initialIsPercentage, initialValue]);

  const handleSave = () => {
    const amount = Number(value);
    if (
      `${value}`.trim() === "" ||
      !Number.isFinite(amount) ||
      amount < 0 ||
      (isPercentage && amount > 100)
    ) {
      errorToast(
        isPercentage
          ? "Enter a discount between 0 and 100%"
          : "Enter a discount of ₹0 or more"
      );
      return;
    }
    setSaving(true);
    dispatch(
      invoiceDiscountUpdateAction(
        { sot_id: sotId, percentage: amount, isPercentage },
        (data) => {
          setSaving(false);
          successToast(data?.message);
          onSaved(data?.data);
          onClose();
        },
        (data) => {
          setSaving(false);
          errorToast(
            data?.data?.message || "Could not update the invoice discount"
          );
        }
      )
    );
  };

  return (
    <Transition show={isOpen ? true : false} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-50 overflow-y-auto"
        onClose={() => !saving && onClose()}
      >
        <div className="flex items-center justify-center min-h-screen px-4 text-center">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-100"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <div className="mx-auto w-full max-w-md my-8 p-6 text-left align-middle transition-all transform bg-white shadow-xl rounded-md">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-medium leading-6 text-gray-900">
                  Edit Invoice Discount
                </h2>
                <button
                  type="button"
                  className="close-button bg-transparent border-none p-0 hover:bg-787878"
                  onClick={onClose}
                  disabled={saving}
                >
                  <CloseIcon />
                </button>
              </div>

              {billNumber && (
                <p className="mt-2 text-sm text-gray-900">
                  Bill No: <span className="font-medium">{billNumber}</span>
                </p>
              )}
              <p className="mt-2 text-sm text-gray-600">
                The bill total is recalculated with the new discount.
              </p>

              <div className="relative mt-4">
                <input
                  type="number"
                  min="0"
                  autoFocus
                  value={value}
                  disabled={saving}
                  placeholder={`Inv Disc ${isPercentage ? "%" : "₹"}`}
                  onChange={(e) => setValue(e.target.value)}
                  onKeyDown={(e) => {
                    // keep the keystroke away from the billing screen's shortcuts
                    e.stopPropagation();
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSave();
                    }
                  }}
                  className="w-full h-[40px] pl-3 pr-[48px] text-sm border border-[var(--items-search-input-border)] rounded focus:outline-none"
                />
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => setIsPercentage(!isPercentage)}
                  className="absolute top-1/2 -translate-y-1/2 right-0 w-[40px] min-h-[40px] flex items-center justify-center bg-[var(--create-tab-foot-subTot-bg)] border border-[var(--items-search-input-border)] rounded-r"
                >
                  {isPercentage ? "%" : "₹"}
                </button>
              </div>

              <div className="flex justify-end gap-3 items-center mt-6">
                <DraftButton
                  isDisabled={saving}
                  className="text-primary border border-primary border-solid px-6 py-2 items-center rounded-md flex justify-center fw_500 text-sm"
                  onclkFn={onClose}
                  text="Cancel"
                />
                <AddButton
                  onclkFn={handleSave}
                  loading={saving}
                  className="bg-primary text-white px-6 py-2 rounded flex items-center justify-center fw_500 text-sm"
                  spinnerClass="h-4 w-4 border-t-2 border-white"
                  text="Save"
                  prefixIcon={false}
                />
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default EditInvoiceDiscountModal;
